/**
 * LineBarChart Component
 *
 * Displays dual-axis chart with bars and line overlay using Recharts ComposedChart.
 * Typically used for actual vs plan comparisons.
 *
 * Performance: Uses ChartDataContext for cached data - no individual API calls
 */

import PropTypes from 'prop-types';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartData } from '../../contexts/ChartDataContext';
import { formatDate, formatMetricValue, formatAxisValue } from '../../utils/formatters';
import './ChartStyles.css';

const LineBarChart = ({ chartId }) => {
  // Get chart data from cache (loaded via batch request in template)
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);

  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="spinner"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className="chart-container no-data">
        <p>No data available</p>
      </div>
    );
  }

  // Extract chart configuration
  const {
    chart_name,
    x_label,
    y_label,
    data_points,
    bar_metric,
    line_metric,
  } = data;

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(label, 'short')}</p>
          {payload.map((entry, index) => {
            // Determine which metric this payload belongs to
            const metric = entry.dataKey === 'bar_value' ? bar_metric : line_metric;
            return (
              <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                {metric.metric_name}: {formatMetricValue(entry.value, metric.metric_unit)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Format X-axis labels
  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }

    // Determine format based on data granularity
    const firstDate = new Date(data_points[0]?.date);
    const lastDate = new Date(data_points[data_points.length - 1]?.date);
    const timeSpanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    if (timeSpanDays <= 90) {
      return formatDate(dateStr, 'short');
    } else if (timeSpanDays <= 730) {
      return formatDate(dateStr, 'month');
    } else {
      return formatDate(dateStr, 'year');
    }
  };

  // Sort data by date
  const sortedData = [...data_points].sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{chart_name}</h3>
        <p className="chart-subtitle">
          {bar_metric.metric_name} vs {line_metric.metric_name}
        </p>
      </div>

      <div className="chart-body">
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={sortedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              yAxisId="left"
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, bar_metric.metric_unit)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, line_metric.metric_unit)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* Bar for first metric (usually actual) */}
            <Bar
              yAxisId="left"
              dataKey="bar_value"
              name={bar_metric.metric_name}
              fill="#3498db"
              radius={[4, 4, 0, 0]}
            />

            {/* Line for second metric (usually plan/target) */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="line_value"
              name={line_metric.metric_name}
              stroke="#e74c3c"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

LineBarChart.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default LineBarChart;
